import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  MapPin,
  Bed,
  Bath,
  Square,
  CheckCircle2,
  Plus,
  ArrowLeft,
  Sparkles,
  Layers,
  ShieldCheck,
  Calendar,
  Wifi,
  Home as HomeIcon,
  Flame,
  Award,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  formatPriceWithConversion,
  formatOriginalPrice,
  BILLING_PERIODS,
} from '../utils/currency';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const MAX_COMPARE = 4;

const defaultImage =
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=800&q=80';

/** Normalizes amenity keys into a friendly label. */
function labelAmenity(a) {
  if (!a) return '';
  return String(a)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** A single comparison column. */
function ColumnHeader({ property, onRemove }) {
  const { viewerCurrency } = useCurrency();
  const cover = property.media?.[0]?.url || defaultImage;

  const priceInfo = formatPriceWithConversion(
    property.monthly_rent,
    property.currency || 'USD',
    viewerCurrency,
    property.billing_period || 'monthly',
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onRemove(property.property_id)}
        aria-label={`Remove ${property.title} from comparison`}
        className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/95 text-slate-700 ring-1 ring-black/5 shadow-md flex items-center justify-center hover:bg-white hover:text-slate-900 transition"
      >
        <X className="w-4 h-4" />
      </button>

      <Link
        to={`/property/${property.property_id}`}
        className="block overflow-hidden rounded-2xl ring-1 ring-slate-200 bg-white shadow-sm hover:shadow-lg transition-shadow"
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={cover}
            alt={property.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
          {property.owner_verified && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white text-[11px] font-medium px-2 py-0.5 shadow">
              <CheckCircle2 className="w-3 h-3" /> Verified
            </span>
          )}
          <div className="absolute bottom-3 left-3">
            <div className="rounded-xl bg-white/95 backdrop-blur-sm px-3 py-1.5 shadow-sm ring-1 ring-black/5">
              <div className="font-bold text-slate-900 text-base leading-tight">
                {priceInfo.original}
              </div>
              {priceInfo.showConversion && (
                <div className="text-[11px] text-slate-500">
                  {priceInfo.converted}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-4">
          <h3 className="font-serif text-base font-semibold text-slate-900 line-clamp-2 min-h-[2.75rem]">
            {property.title}
          </h3>
          <div className="mt-1 flex items-start gap-1 text-slate-500 text-[13px]">
            <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span className="line-clamp-2">
              {[property.location?.area, property.location?.city, property.location?.country]
                .filter(Boolean)
                .join(', ') || 'Location not specified'}
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}

/** Placeholder "Add listing" column shown when slots are free. */
function AddColumn({ remaining }) {
  return (
    <Link
      to="/search"
      className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 text-slate-500 p-6 hover:border-accent hover:text-accent hover:bg-accent/5 transition min-h-[13rem]"
    >
      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-2">
        <Plus className="w-5 h-5" />
      </div>
      <div className="text-sm font-medium">Add a listing</div>
      <div className="text-[11px] text-slate-400 mt-0.5">
        {remaining} slot{remaining === 1 ? '' : 's'} left
      </div>
    </Link>
  );
}

/**
 * Row renderer: consistent cell styling, highlights the "winning" cell when
 * there's a meaningful best value (cheapest price, most space, etc.).
 */
function Row({ label, icon: Icon, cells, highlightIndex = -1 }) {
  return (
    <div className="grid grid-cols-[minmax(120px,160px)_1fr] md:grid-cols-[minmax(160px,200px)_1fr] border-b border-slate-200/70 last:border-b-0">
      <div className="py-4 pr-4 text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div
        className="py-4 grid gap-3"
        style={{
          gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))`,
        }}
      >
        {cells.map((cell, i) => (
          <div
            key={i}
            className={`min-w-0 text-sm leading-relaxed px-3 py-2 rounded-lg ${
              i === highlightIndex
                ? 'bg-accent/8 text-slate-900 ring-1 ring-accent/25 font-medium'
                : 'text-slate-700'
            }`}
          >
            {cell ?? <span className="text-slate-300">—</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Compare() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { viewerCurrency } = useCurrency();

  const ids = useMemo(() => {
    const raw = searchParams.get('ids') || '';
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, MAX_COMPARE);
  }, [searchParams]);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (ids.length === 0) {
      setProperties([]);
      return undefined;
    }
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const results = await Promise.all(
          ids.map(async (id) => {
            try {
              const r = await fetch(`${API_URL}/api/properties/${id}`);
              if (!r.ok) return null;
              return await r.json();
            } catch {
              return null;
            }
          }),
        );
        if (cancelled) return;
        const valid = results.filter(Boolean);
        setProperties(valid);

        // Prune ids that didn't resolve so the URL stays tidy.
        if (valid.length !== ids.length) {
          const next = valid.map((p) => p.property_id).join(',');
          setSearchParams(
            next ? { ids: next } : {},
            { replace: true },
          );
        }
      } catch (e) {
        if (!cancelled) setError('Could not load one or more listings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  const handleRemove = useCallback(
    (id) => {
      const next = ids.filter((x) => x !== id);
      setSearchParams(next.length ? { ids: next.join(',') } : {}, { replace: false });
      toast.success('Removed from comparison');
    },
    [ids, setSearchParams],
  );

  const handleClearAll = useCallback(() => {
    setSearchParams({}, { replace: false });
  }, [setSearchParams]);

  // Derived: best-value indexes for highlighting.
  const bestIndexes = useMemo(() => {
    if (properties.length < 2) return {};
    const rentUSD = properties.map((p) => {
      // normalize to USD for comparison (display stays per-listing)
      const info = formatPriceWithConversion(
        p.monthly_rent,
        p.currency || 'USD',
        'USD',
        p.billing_period || 'monthly',
      );
      // parseable number fallback
      return p.monthly_rent ?? 0;
    });
    const cheapest = rentUSD.indexOf(Math.min(...rentUSD));
    const largest = (() => {
      const arr = properties.map((p) => p.details?.area_sqft || 0);
      const max = Math.max(...arr);
      return max > 0 ? arr.indexOf(max) : -1;
    })();
    const mostBeds = (() => {
      const arr = properties.map((p) => p.details?.bedroom_count || 0);
      const max = Math.max(...arr);
      return max > 0 ? arr.indexOf(max) : -1;
    })();
    return { cheapest, largest, mostBeds };
  }, [properties]);

  // Union of amenities so every row lines up.
  const allAmenities = useMemo(() => {
    const set = new Set();
    properties.forEach((p) => {
      (p.amenities || []).forEach((a) => set.add(a));
    });
    return Array.from(set).sort();
  }, [properties]);

  const emptySlots = MAX_COMPARE - properties.length;

  return (
    <div className="min-h-screen bg-cream-100" data-testid="compare-page">
      <SEOHead
        title="Compare Listings · RoomHora"
        description="Compare rental listings side-by-side on RoomHora — price, size, amenities, and owner verification at a glance."
      />
      <Navbar />

      {/* Hero band */}
      <header className="border-b border-slate-200/70 bg-white">
        <div className="rh-container pt-28 pb-10">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-4"
          >
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                <Layers className="w-3.5 h-3.5" /> Compare
              </div>
              <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] text-slate-900">
                Side-by-side, nothing hidden.
              </h1>
              <p className="mt-3 max-w-xl text-base text-slate-600 leading-relaxed">
                Compare up to {MAX_COMPARE} homes on price, space, amenities, and
                owner verification — all in one view.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-700 px-4 py-2 text-sm hover:bg-slate-50 transition"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              {properties.length > 0 && (
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-sm text-slate-500 hover:text-slate-800 underline-offset-4 hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </header>

      <main className="rh-container py-10 md:py-14">
        {loading && (
          <div className="flex items-center gap-3 text-slate-500 mb-6">
            <div className="w-5 h-5 border-2 border-accent border-t-transparent rounded-full animate-spin" />
            Loading listings…
          </div>
        )}

        {!loading && error && (
          <div className="rounded-xl bg-rose-50 text-rose-700 ring-1 ring-rose-200 px-4 py-3 text-sm mb-6">
            {error}
          </div>
        )}

        {!loading && properties.length === 0 && (
          <EmptyState />
        )}

        {!loading && properties.length > 0 && (
          <>
            {/* Header cards row */}
            <div
              className="grid gap-4 md:gap-6"
              style={{
                gridTemplateColumns: `repeat(${Math.min(
                  MAX_COMPARE,
                  Math.max(properties.length + (emptySlots > 0 ? 1 : 0), 1),
                )}, minmax(0, 1fr))`,
              }}
            >
              <AnimatePresence mode="popLayout">
                {properties.map((p) => (
                  <motion.div
                    key={p.property_id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ColumnHeader property={p} onRemove={handleRemove} />
                  </motion.div>
                ))}
              </AnimatePresence>
              {emptySlots > 0 && <AddColumn remaining={emptySlots} />}
            </div>

            {/* Comparison table */}
            <div className="mt-10 rounded-3xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 md:px-8 pt-6 pb-2 border-b border-slate-200/70 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-accent" />
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500">
                  Details at a glance
                </div>
              </div>
              <div className="px-5 md:px-8">
                <Row
                  label="Monthly rent"
                  icon={Flame}
                  highlightIndex={bestIndexes.cheapest}
                  cells={properties.map((p) => (
                    <div>
                      <div className="font-semibold text-slate-900">
                        {
                          formatPriceWithConversion(
                            p.monthly_rent,
                            p.currency || 'USD',
                            viewerCurrency,
                            p.billing_period || 'monthly',
                          ).original
                        }
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {BILLING_PERIODS?.[p.billing_period || 'monthly']?.label ||
                          'Monthly'}
                      </div>
                    </div>
                  ))}
                />
                <Row
                  label="Deposit"
                  cells={properties.map((p) =>
                    p.deposit > 0 ? (
                      <span>
                        {formatOriginalPrice(
                          p.deposit,
                          p.currency || 'USD',
                        )}
                        {p.negotiable && (
                          <span className="ml-2 text-emerald-600 text-xs">
                            Negotiable
                          </span>
                        )}
                      </span>
                    ) : null,
                  )}
                />
                <Row
                  label="Bedrooms"
                  icon={Bed}
                  highlightIndex={bestIndexes.mostBeds}
                  cells={properties.map((p) =>
                    p.details?.bedroom_count ? (
                      <span>{p.details.bedroom_count}</span>
                    ) : null,
                  )}
                />
                <Row
                  label="Bathrooms"
                  icon={Bath}
                  cells={properties.map((p) =>
                    p.details?.bathroom_count ? (
                      <span>{p.details.bathroom_count}</span>
                    ) : null,
                  )}
                />
                <Row
                  label="Area"
                  icon={Square}
                  highlightIndex={bestIndexes.largest}
                  cells={properties.map((p) =>
                    p.details?.area_sqft ? (
                      <span>{p.details.area_sqft} sqft</span>
                    ) : null,
                  )}
                />
                <Row
                  label="Property type"
                  icon={HomeIcon}
                  cells={properties.map((p) =>
                    p.property_type ? (
                      <span className="capitalize">
                        {String(p.property_type).replace(/_/g, ' ')}
                      </span>
                    ) : null,
                  )}
                />
                <Row
                  label="Verified owner"
                  icon={ShieldCheck}
                  cells={properties.map((p) =>
                    p.owner_verified ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 className="w-4 h-4" /> Verified
                      </span>
                    ) : (
                      <span className="text-slate-400">Not verified</span>
                    ),
                  )}
                />
                <Row
                  label="Available"
                  icon={Calendar}
                  cells={properties.map((p) =>
                    p.available_from ? (
                      <span>
                        {new Date(p.available_from).toLocaleDateString(
                          undefined,
                          { year: 'numeric', month: 'short', day: 'numeric' },
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400">Flexible</span>
                    ),
                  )}
                />
                <Row
                  label="Location"
                  icon={MapPin}
                  cells={properties.map((p) => (
                    <span>
                      {[
                        p.location?.area,
                        p.location?.city,
                        p.location?.country,
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </span>
                  ))}
                />

                {allAmenities.length > 0 && (
                  <div className="py-5 border-t border-slate-200/70">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500 mb-4">
                      <Wifi className="w-3.5 h-3.5" /> Amenities
                    </div>
                    <div
                      className="grid gap-3"
                      style={{
                        gridTemplateColumns: `repeat(${properties.length}, minmax(0, 1fr))`,
                      }}
                    >
                      {properties.map((p, i) => {
                        const has = new Set(p.amenities || []);
                        return (
                          <ul
                            key={p.property_id + '-amen'}
                            className="space-y-1.5 text-sm"
                          >
                            {allAmenities.map((a) => (
                              <li
                                key={a}
                                className={`flex items-center gap-2 ${
                                  has.has(a)
                                    ? 'text-slate-800'
                                    : 'text-slate-300 line-through'
                                }`}
                              >
                                {has.has(a) ? (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                ) : (
                                  <X className="w-4 h-4 flex-shrink-0" />
                                )}
                                <span className="truncate">{labelAmenity(a)}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      })}
                    </div>
                  </div>
                )}

                {properties.some((p) => (p.rating_average || 0) > 0) && (
                  <Row
                    label="Rating"
                    icon={Award}
                    cells={properties.map((p) =>
                      p.rating_average ? (
                        <span>
                          <strong className="text-slate-900">
                            {Number(p.rating_average).toFixed(1)}
                          </strong>
                          <span className="text-slate-500 text-xs">
                            {' '}
                            ({p.rating_count || 0})
                          </span>
                        </span>
                      ) : null,
                    )}
                  />
                )}
              </div>

              {/* Footer CTA strip */}
              <div
                className="bg-slate-50 border-t border-slate-200 px-5 md:px-8 py-5 grid gap-3"
                style={{
                  gridTemplateColumns: `minmax(160px,200px) repeat(${properties.length}, minmax(0,1fr))`,
                }}
              >
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500 self-center">
                  Next step
                </div>
                {properties.map((p) => (
                  <Link
                    key={p.property_id + '-cta'}
                    to={`/property/${p.property_id}`}
                    className="inline-flex items-center justify-center rounded-full bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-700 transition"
                  >
                    View details
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl bg-white ring-1 ring-slate-200 p-10 md:p-16 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-accent/10 text-accent flex items-center justify-center mb-5">
        <Layers className="w-6 h-6" />
      </div>
      <h2 className="font-serif text-2xl md:text-3xl text-slate-900 tracking-[-0.01em]">
        Pick a few places to compare
      </h2>
      <p className="mt-3 max-w-md mx-auto text-slate-600">
        From any listing, tap the <strong>Compare</strong> button to add it here.
        You can line up to {MAX_COMPARE} homes side-by-side and see what
        actually differs.
      </p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <Link
          to="/search"
          className="inline-flex items-center gap-2 rounded-full bg-accent text-white px-5 py-2.5 text-sm font-medium hover:bg-accent/90 transition"
        >
          Browse listings
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-700 px-5 py-2.5 text-sm font-medium hover:bg-slate-50 transition"
        >
          Back home
        </Link>
      </div>
    </div>
  );
}
